/**
 * Following a pipeline run until the SERVER says it is over.
 *
 * `runPipeline` used to poll for 60 s and then call the run failed. A run is
 * allowed to take as long as it takes (an LLM step over thousands of rows runs
 * for many minutes), and the 60 s was the client's patience, not the run's
 * state: the pipeline kept running while the editor showed a red failure and
 * wrote a FAILED entry into the run log. The only things that end a follow now
 * are a terminal status from the server, a run the server no longer knows, or
 * the caller stopping it. A slow or failing request never does.
 */

export interface RunSnapshot {
  run_id?: string;
  status: string;
  rows_in?: number;
  rows_out?: number;
  error_message?: string | null;
  started_at?: string | null;
  finished_at?: string | null;
  node_audits?: Record<string, unknown> | null;
  current_node_id?: string | null;
  current_node_label?: string | null;
  current_step_index?: number | null;
  total_steps?: number | null;
  current_node_processed?: number | null;
  current_node_total?: number | null;
}

/** What the editor shows while a run is in flight. */
export interface RunProgress {
  runId: string;
  status: string;
  startedAt: string;
  /** Step the executor is on, e.g. "FILTER · Solo activos". */
  stepLabel?: string;
  stepIndex?: number;
  totalSteps?: number;
  processed?: number;
  total?: number;
}

/** A run in one of these is still going. Anything else is over. */
const IN_FLIGHT = new Set(['RUNNING', 'PENDING', 'QUEUED', 'STARTING']);
export const isRunInFlight = (status: string | undefined | null): boolean =>
  IN_FLIGHT.has(String(status ?? '').toUpperCase());

/** Poll cadence: quick at first, relaxed for long runs. */
export const pollDelayMs = (tick: number): number => (tick < 30 ? 2000 : 5000);

/** Consecutive 404s before we conclude the run no longer exists (~30 s). */
export const MAX_MISSING = 15;

export const toProgress = (snap: RunSnapshot, runId: string, fallbackStart: string): RunProgress => ({
  runId,
  status: snap.status,
  startedAt: snap.started_at || fallbackStart,
  stepLabel: snap.current_node_label || undefined,
  stepIndex: snap.current_step_index ?? undefined,
  totalSteps: snap.total_steps ?? undefined,
  processed: snap.current_node_processed ?? undefined,
  total: snap.current_node_total ?? undefined,
});

export interface FollowDeps {
  /** One read of the run. `status` is the HTTP status; 0 means the request itself failed. */
  fetchRun: () => Promise<{ status: number; body?: RunSnapshot }>;
  sleep: (ms: number) => Promise<void>;
  onTick?: (snap: RunSnapshot) => void;
  /** Cooperative stop (caller went away). */
  shouldStop?: () => boolean;
}

export type FollowOutcome =
  | { kind: 'finished'; run: RunSnapshot }
  | { kind: 'lost' }       // the server no longer has that run
  | { kind: 'stopped' };   // the caller asked us to stop

export async function followRun(deps: FollowDeps): Promise<FollowOutcome> {
  let missing = 0;
  for (let tick = 0; ; tick++) {
    if (deps.shouldStop?.()) return { kind: 'stopped' };
    let res: { status: number; body?: RunSnapshot };
    try {
      res = await deps.fetchRun();
    } catch {
      res = { status: 0 };
    }
    if (res.status === 404) {
      missing += 1;
      if (missing >= MAX_MISSING) return { kind: 'lost' };
    } else {
      missing = 0;
      if (res.body && res.status >= 200 && res.status < 300) {
        deps.onTick?.(res.body);
        if (!isRunInFlight(res.body.status)) return { kind: 'finished', run: res.body };
      }
      // 5xx, network drop, timeout: the run does not care, keep following.
    }
    await deps.sleep(pollDelayMs(tick));
  }
}
